/**
 * SignatureTrackCredit — a 2-second whisper of authorship at the start
 * of every immersion. Fades in, holds, fades out. NEVER persists on
 * screen. Designed to be elegant enough that users feel the bespoke
 * nature of the track without ever being told outright.
 *
 * Renders in the lower-right corner with a hairline gold rule above
 * the track name. Pure typography — no chrome, no buttons.
 *
 * Props
 *   name  (string)   The track display name, e.g. "Absorb Engine"
 *   show  (bool)     Optional — gate the reveal until the parent is ready
 *   delay (number)   ms before fade-in starts (default 1200)
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const C = {
  cream:   "#F3EDDD",
  creamDim:"rgba(243, 237, 221, 0.55)",
  gold:    "#D7BB7A",
};

export default function SignatureTrackCredit({ name, show = true, delay = 1200 }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!show || !name) return undefined;
    const t1 = setTimeout(() => setVisible(true),  delay);
    const t2 = setTimeout(() => setVisible(false), delay + 5200); // 2s fade-in + 3s hold (~5.2s total)
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [show, name, delay]);

  if (!name) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -2 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="absolute pointer-events-none z-10"
          style={{ right: "1.25rem", bottom: "1.4rem" }}
          data-testid="signature-track-credit"
        >
          {/* Hairline gold rule */}
          <div
            style={{
              height: 1,
              width: 28,
              background: `linear-gradient(90deg, transparent 0%, ${C.gold} 100%)`,
              opacity: 0.7,
              marginLeft: "auto",
              marginBottom: 6,
            }}
            aria-hidden
          />
          <p
            style={{
              fontFamily: '"Manrope", sans-serif',
              fontWeight: 500,
              fontSize: "0.6rem",
              letterSpacing: "0.36em",
              textTransform: "uppercase",
              color: C.creamDim,
              lineHeight: 1.2,
              textAlign: "right",
              textShadow: "0 2px 16px rgba(0,0,0,0.55)",
            }}
          >
            {name}
          </p>
          <p
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "0.78rem",
              letterSpacing: "0.04em",
              color: C.cream,
              lineHeight: 1.2,
              textAlign: "right",
              marginTop: 2,
              textShadow: "0 2px 18px rgba(0,0,0,0.55)",
            }}
          >
            Reclaim Original
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
